import React from 'react';
import {
  Box,
  Typography,
  Divider,
  Button,
  Avatar,
  IconButton,
  Tooltip,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  AccessTime as TimeIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Description as DescriptionIcon,
  Language as WebsiteIcon,
  NavigateNext as NavigateNextIcon,
  Forum as ForumIcon,
  History as HistoryIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  Category as CategoryIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';

// Date formatter
const formatDate = (dateString) => {
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  return new Date(dateString).toLocaleDateString(undefined, options);
};

const DetailsSidebar = ({
  ticket,
  users,
  onlineUsers,
  theme,
  isMobile,
  onBackToChat
}) => {
  if (!ticket) {
    return (
      <Box
        sx={{
          width: 300,
          height: '100%',
          background: 'rgba(22, 28, 36, 0.8)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography color="rgba(255,255,255,0.5)">No ticket selected</Typography>
      </Box>
    );
  }

  const statusColor =
    ticket.status === 'urgent'
      ? '#F44336'
      : ticket.status === 'open'
        ? '#4C85F0'
        : ticket.status === 'pending'
          ? '#FF9800'
          : '#66BB6A';

  return (
    <Paper
      elevation={0}
      sx={{
        width: { xs: '100%', lg: 300 },
        height: '100%',
        background: 'rgba(22, 28, 36, 0.8)',
        backdropFilter: 'blur(10px)',
        borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        {isMobile && (
          <IconButton edge="start" color="inherit" onClick={onBackToChat} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
        )}
        <Typography variant="h6" sx={{ color: 'white', flexGrow: 1 }}>
          Ticket Details
        </Typography>
        <IconButton size="small" sx={{ color: 'rgba(255,255,255,0.7)' }}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 0 }}>
        {/* Customer information */}
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar
              src={ticket.customer.avatar}
              alt={ticket.customer.name}
              sx={{ width: 50, height: 50, mr: 2 }}
            >
              {ticket.customer.name.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 'medium' }}>
                {ticket.customer.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: statusColor
                  }}
                />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  {ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}
                </Typography>
              </Box>
            </Box>
          </Box>
          
          <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />
          
          {/* Ticket information */}
          <Typography variant="subtitle2" sx={{ color: 'white', mb: 2 }}>
            Ticket Information
          </Typography>
          
          <List dense disablePadding>
            <ListItem sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36, color: 'rgba(255,255,255,0.7)' }}>
                <AssignmentIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary={
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    {ticket.id}
                  </Typography>
                }
                secondary="Ticket ID"
                secondaryTypographyProps={{ variant: 'caption', color: 'rgba(255,255,255,0.5)' }}
              />
            </ListItem>
            
            <ListItem sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36, color: 'rgba(255,255,255,0.7)' }}>
                <TimeIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary={
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    {formatDate(ticket.createdAt)}
                  </Typography>
                }
                secondary="Created"
                secondaryTypographyProps={{ variant: 'caption', color: 'rgba(255,255,255,0.5)' }}
              />
            </ListItem>
            
            <ListItem sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36, color: 'rgba(255,255,255,0.7)' }}>
                <CategoryIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary={
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    {ticket.category.charAt(0).toUpperCase() + ticket.category.slice(1)}
                  </Typography>
                }
                secondary="Category"
                secondaryTypographyProps={{ variant: 'caption', color: 'rgba(255,255,255,0.5)' }}
              />
            </ListItem>
            
            <ListItem sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36, color: 'rgba(255,255,255,0.7)' }}>
                <PriorityIcon fontSize="small" priority={ticket.priority} />
              </ListItemIcon>
              <ListItemText 
                primary={
                  <Typography variant="body2" sx={{ color: getPriorityColor(ticket.priority) }}>
                    {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}
                  </Typography>
                }
                secondary="Priority"
                secondaryTypographyProps={{ variant: 'caption', color: 'rgba(255,255,255,0.5)' }}
              />
            </ListItem>
          </List>
        </Box>
        
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        
        {/* Contact Information */}
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ color: 'white', mb: 2 }}>
            Contact Information
          </Typography>
          
          <List dense disablePadding>
            <ListItem sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36, color: 'rgba(255,255,255,0.7)' }}>
                <EmailIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary={
                  <Link 
                    href={`mailto:${ticket.customer.email}`} 
                    sx={{ 
                      color: theme.palette.primary.main, 
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    {ticket.customer.email}
                  </Link>
                }
                secondary="Email"
                secondaryTypographyProps={{ variant: 'caption', color: 'rgba(255,255,255,0.5)' }}
              />
            </ListItem>
            
            {ticket.customer.phone && (
              <ListItem sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36, color: 'rgba(255,255,255,0.7)' }}>
                  <PhoneIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary={
                    <Link 
                      href={`tel:${ticket.customer.phone}`} 
                      sx={{ 
                        color: theme.palette.primary.main, 
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      {ticket.customer.phone}
                    </Link>
                  }
                  secondary="Phone"
                  secondaryTypographyProps={{ variant: 'caption', color: 'rgba(255,255,255,0.5)' }}
                />
              </ListItem>
            )}
          </List>
        </Box>
        
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        
        {/* Assign to and status */}
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ color: 'white', mb: 2 }}>
            Assignment
          </Typography>
          
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel 
              id="assign-label" 
              sx={{ 
                color: 'rgba(255,255,255,0.7)',
                '&.Mui-focused': {
                  color: theme.palette.primary.main
                }
              }}
            >
              Assign To
            </InputLabel>
            <Select
              labelId="assign-label"
              value={ticket.assignedTo || ''}
              label="Assign To"
              displayEmpty
              onChange={() => {}}
              sx={{
                color: 'white',
                '.MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255,255,255,0.2)'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255,255,255,0.3)'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main
                }
              }}
            >
              <MenuItem value="">
                <em>Unassigned</em>
              </MenuItem>
              {users.map(user => (
                <MenuItem key={user.id} value={user.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{ 
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: onlineUsers.includes(user.id) ? '#4caf50' : 'grey',
                        mr: 1
                      }}
                    />
                    {user.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth size="small">
            <InputLabel 
              id="status-label" 
              sx={{ 
                color: 'rgba(255,255,255,0.7)',
                '&.Mui-focused': {
                  color: theme.palette.primary.main
                }
              }}
            >
              Status
            </InputLabel>
            <Select
              labelId="status-label"
              value={ticket.status}
              label="Status"
              onChange={() => {}}
              sx={{
                color: 'white',
                '.MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255,255,255,0.2)'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255,255,255,0.3)'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main
                }
              }}
            >
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="urgent">Urgent</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </Select>
          </FormControl>
        </Box>
        
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        
        {/* Resources and Quick Links */}
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ color: 'white', mb: 2 }}>
            Helpful Resources
          </Typography>
          
          <List dense disablePadding>
            <ListItem 
              button
              sx={{ 
                px: 1, 
                py: 1, 
                borderRadius: 1,
                mb: 1,
                backgroundColor: 'rgba(255,255,255,0.05)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: theme.palette.primary.main }}>
                <DescriptionIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="Company Policy"
                sx={{ color: 'white' }}
              />
              <NavigateNextIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.5)' }} />
            </ListItem>
            
            <ListItem 
              button
              sx={{ 
                px: 1, 
                py: 1, 
                borderRadius: 1,
                mb: 1,
                backgroundColor: 'rgba(255,255,255,0.05)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: theme.palette.primary.main }}>
                <ReceiptIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="Refund Process"
                sx={{ color: 'white' }}
              />
              <NavigateNextIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.5)' }} />
            </ListItem>
            
            <ListItem 
              button
              sx={{ 
                px: 1, 
                py: 1, 
                borderRadius: 1,
                mb: 1,
                backgroundColor: 'rgba(76, 133, 240, 0.15)',
                '&:hover': {
                  backgroundColor: 'rgba(76, 133, 240, 0.2)'
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: theme.palette.primary.main }}>
                <WebsiteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="BetFI Presale"
                sx={{ color: 'white' }}
              />
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  backgroundColor: 'rgba(255,69,0,0.2)',
                  color: '#FF4500',
                  borderRadius: '4px',
                  padding: '2px 8px',
                  fontSize: '0.7rem',
                  fontWeight: 'bold'
                }}
              >
                HOT
              </Box>
            </ListItem>
          </List>
        </Box>
      </Box>
    </Paper>
  );
};

// Helper components
const PriorityIcon = ({ priority, ...props }) => {
  switch(priority) {
    case 'high':
      return <Box 
        component="span"
        sx={{
          width: 20,
          height: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        {...props}
      >
        <ArrowUpwardIcon fontSize="small" sx={{ color: '#F44336' }} />
      </Box>;
    case 'medium':
      return <Box 
        component="span"
        sx={{
          width: 20,
          height: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        {...props}
      >
        <RemoveIcon fontSize="small" sx={{ color: '#FF9800' }} />
      </Box>;
    default:
      return <Box 
        component="span"
        sx={{
          width: 20,
          height: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        {...props}
      >
        <ArrowDownwardIcon fontSize="small" sx={{ color: '#4CAF50' }} />
      </Box>;
  }
};

// Helper function to get priority color
const getPriorityColor = (priority) => {
  switch(priority) {
    case 'high':
      return '#F44336';
    case 'medium':
      return '#FF9800';
    default:
      return '#4CAF50';
  }
};

export default DetailsSidebar;
